import type { ChatbotConversation } from '@/models/chatbot';

export type ChatHistoryGroupId = 'today' | 'yesterday' | 'lastWeek' | 'older';

export interface ChatHistoryGroup {
  id: ChatHistoryGroupId;
  conversations: ChatbotConversation[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const GROUP_ORDER: ChatHistoryGroupId[] = ['today', 'yesterday', 'lastWeek', 'older'];

const startOfDay = (timestamp: number): number => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const getGroupId = (updatedAtSeconds: number | undefined, now: number): ChatHistoryGroupId => {
  if (typeof updatedAtSeconds !== 'number') {
    return 'older';
  }

  const todayStart = startOfDay(now);
  const updatedMs = updatedAtSeconds * 1000;

  if (updatedMs >= todayStart) {
    return 'today';
  }
  if (updatedMs >= todayStart - DAY_MS) {
    return 'yesterday';
  }
  if (updatedMs >= todayStart - 6 * DAY_MS) {
    return 'lastWeek';
  }
  return 'older';
};

export const groupConversationsByDate = (
  conversations: ChatbotConversation[],
  now: number = Date.now(),
): ChatHistoryGroup[] => {
  const buckets = new Map<ChatHistoryGroupId, ChatbotConversation[]>();

  const orderedByLastInteraction = [...conversations].sort(
    (first, second) => (second.updated_at ?? 0) - (first.updated_at ?? 0),
  );

  orderedByLastInteraction.forEach(conversation => {
    const groupId = getGroupId(conversation.updated_at, now);
    const existing = buckets.get(groupId) ?? [];
    existing.push(conversation);
    buckets.set(groupId, existing);
  });

  return GROUP_ORDER.flatMap(id => {
    const items = buckets.get(id);
    return items && items.length > 0 ? [{ id, conversations: items }] : [];
  });
};
